import type { ConciseBranch } from '@festina/configuration-general-types/configuration'
import type { BranchKey } from '@festina-life/api'
import { assertNotEmpty, type Optional } from '@festina-life/core'
import { Alert } from '@festina-life/ui/feedback'
import { Select } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { Stack } from '@festina-life/ui/structure'
import { useState } from 'react'

import { MergeBranchForm } from './MergeBranchForm'
import { useNonAncestorBranchesOnServer } from './useNonAncestorBranchesOnServer'

type Props = {
  branchKey: BranchKey
  onDidMerge: (revalidateWorkscapes: boolean) => void
}

export const MergeBranchSelector = ({ branchKey, onDidMerge }: Props) => {
  const translate = useTranslate()
  const mergeBranchCandidates = useNonAncestorBranchesOnServer(branchKey)
  const [mergeSource, setMergeSource] = useState(mergeBranchCandidates.at(0))

  if (!mergeSource) {
    return (
      <Alert
        severity="warning"
        title={translate('branchDetailsPanel.tabs.manage.merge.noBranches', {
          server: branchKey.server,
        })}
      />
    )
  }

  const handleMergeSourceChanged = (sourceBranch: Optional<ConciseBranch>) => {
    if (sourceBranch) {
      setMergeSource(sourceBranch)
    }
  }

  assertNotEmpty(mergeSource.name)

  return (
    <Stack gap="display" className="flex-col">
      <Select
        wrapperProps={{
          label: translate(
            'branchDetailsPanel.tabs.manage.merge.form.sourceBranch.label',
          ),
          description: translate(
            'branchDetailsPanel.tabs.manage.merge.form.sourceBranch.description',
            { targetBranchName: branchKey.name },
          ),
          htmlFor: 'sourceBranch',
        }}
        id="sourceBranch"
        options={mergeBranchCandidates}
        renderFn={({ name }) => name ?? ''}
        keyFn={(conciseBranch) => conciseBranch.name ?? ''}
        shouldShowClearButton={false}
        onChange={handleMergeSourceChanged}
        value={mergeSource}
      />
      <MergeBranchForm
        key={mergeSource.name}
        sourceBranchKey={{ name: mergeSource.name, server: branchKey.server }}
        targetBranchKey={branchKey}
        onDidMerge={onDidMerge}
      />
    </Stack>
  )
}
